import { useEffect } from 'react';

import useNode from '@hooks/use-node';

const DEFAULT_OPTIONS = {
  rootMargin: '0px 0px 0px 0px',
  threshold: [0, 1],
};

/**
 * React hook that fires a callback when the intersection state of the node being watched changes
 *
 * @param {IntersectionObserverCallback} callback Function fired on instersection mutation
 * @param {IntersectionObserverInit} options
 */
function useIntersection(
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit = {}
): ((node: HTMLElement | null) => void)[] {
  const [rootNode, rootRef] = useNode();
  const [targetNode, targetRef] = useNode();

  const { root, rootMargin, threshold } = options;

  useEffect(() => {
    // Create an observer instance linked to the callback function
    if (rootNode && targetNode) {
      const observer = new IntersectionObserver(callback, {
        root: root ?? rootNode,
        rootMargin: rootMargin ?? DEFAULT_OPTIONS.rootMargin,
        threshold: threshold ?? DEFAULT_OPTIONS.threshold,
      });

      // Start observing the target node for configured mutations
      observer.observe(targetNode);
      return () => observer.disconnect();
    }
  }, [rootNode, targetNode, callback, root, rootMargin, threshold]);

  return [rootRef, targetRef];
}

export default useIntersection;
